/**
 * Visitenkarten (.vcf) lesen — aus einem Mail-Anhang wird ein Kontakt.
 *
 * Visitenkarten kommen im Alltag als Mail-Anhang; bisher blieb nur, die Felder
 * von Hand abzutippen. Dieser Parser holt heraus, was die Anlage-Maske
 * aufnehmen kann: Name, Firma, E-Mail, Telefon, Mobil. Alles Weitere (Adresse,
 * Position, Geburtstag, Foto) wird bewusst NICHT gelesen — das Formular hat
 * keine Felder dafür, und ein Wert, der nirgends ankommt, wäre nur eine
 * Verabredung, die niemand einhält.
 *
 * Das Format ist älter als man denkt und entsprechend eigenwillig. Drei
 * Eigenheiten muss ein Leser aushalten, sonst kommt Unsinn heraus:
 *
 *  1. **Gefaltete Zeilen.** Ein Wert darf über mehrere Zeilen laufen; die
 *     Fortsetzung beginnt mit einem Leerzeichen oder Tab (RFC 6350 §3.2).
 *     Wer das ignoriert, verliert alles ab Zeichen 75.
 *  2. **Quoted-Printable** (vCard 2.1, immer noch verbreitet — Outlook schreibt
 *     es bis heute): Umlaute stehen als `=C3=BC`, und `=` am Zeilenende ist ein
 *     weicher Umbruch.
 *  3. **Gruppen-Präfixe** wie `item1.EMAIL` (Apple). Ohne Abschneiden findet
 *     man die Eigenschaft nicht.
 *
 * Der Zeichensatz wird beim Einlesen entschieden, nicht hier — siehe
 * {@link dekodiereVcf}.
 */

/** Was eine Visitenkarte für die Kontaktanlage hergibt. */
export type Visitenkarte = {
    vorname: string;
    nachname: string;
    firma: string;
    email: string;
    telefon: string;
    mobil: string;
};

type Eigenschaft = {
    name: string;
    params: Record<string, string[]>;
    wert: string;
};

/** Entfaltet die Zeilen einer vCard (Regel 1 im Kopfkommentar). */
function entfalte(text: string): string[] {
    const zeilen = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
    const out: string[] = [];
    for (const z of zeilen) {
        if (/^[ \t]/.test(z) && out.length > 0) {
            out[out.length - 1] += z.slice(1);
            continue;
        }
        // Quoted-Printable-Fortsetzung: ein `=` am Zeilenende bindet die
        // nächste Zeile an (Regel 2). Nur dort, wo QP auch angesagt ist.
        const vorher = out[out.length - 1];
        if (vorher !== undefined && /=$/.test(vorher) && /QUOTED-PRINTABLE/i.test(vorher)) {
            out[out.length - 1] = vorher.slice(0, -1) + z;
            continue;
        }
        out.push(z);
    }
    return out;
}

function dekodiereQuotedPrintable(wert: string): string {
    const bytes: number[] = [];
    for (let i = 0; i < wert.length; i++) {
        if (wert[i] === "=" && /^[0-9a-f]{2}$/i.test(wert.slice(i + 1, i + 3))) {
            bytes.push(parseInt(wert.slice(i + 1, i + 3), 16));
            i += 2;
            continue;
        }
        // Alles andere als UTF-8 ausgeben, damit reine ASCII-Werte unverändert
        // bleiben und Mehrbyte-Folgen zusammenbleiben.
        for (const b of new TextEncoder().encode(wert[i])) bytes.push(b);
    }
    try {
        return new TextDecoder("utf-8", { fatal: false }).decode(new Uint8Array(bytes));
    } catch {
        return wert;
    }
}

/** Löst die Maskierungen im Wert auf (`\,` `\;` `\n`). */
function entmaskiere(wert: string): string {
    return wert
        .replace(/\\n/gi, "\n")
        .replace(/\\,/g, ",")
        .replace(/\\;/g, ";")
        .replace(/\\\\/g, "\\");
}

function lesEigenschaft(zeile: string): Eigenschaft | null {
    const doppelpunkt = zeile.indexOf(":");
    if (doppelpunkt < 1) return null;
    const kopf = zeile.slice(0, doppelpunkt);
    let wert = zeile.slice(doppelpunkt + 1);

    const teile = kopf.split(";");
    // Gruppen-Präfix abschneiden (Regel 3).
    const name = (teile[0].includes(".") ? teile[0].slice(teile[0].indexOf(".") + 1) : teile[0])
        .trim()
        .toUpperCase();

    const params: Record<string, string[]> = {};
    for (const p of teile.slice(1)) {
        const gleich = p.indexOf("=");
        // vCard 2.1 schreibt Typen ohne Schlüssel: `TEL;CELL:…`
        const schluessel = (gleich < 0 ? "TYPE" : p.slice(0, gleich)).trim().toUpperCase();
        const roh = gleich < 0 ? p : p.slice(gleich + 1);
        const werte = roh.split(",").map((v) => v.trim().replace(/^"|"$/g, "").toUpperCase());
        params[schluessel] = [...(params[schluessel] ?? []), ...werte];
    }

    if ((params.ENCODING ?? []).some((e) => e === "QUOTED-PRINTABLE")) {
        wert = dekodiereQuotedPrintable(wert);
    }
    return { name, params, wert };
}

const typen = (e: Eigenschaft): string[] => [
    ...(e.params.TYPE ?? []),
    // Apple/Outlook schreiben den Typ gelegentlich als eigenen Schlüssel.
    ...Object.keys(e.params).filter((k) => k !== "TYPE" && k !== "ENCODING" && k !== "CHARSET"),
];

const istMobil = (e: Eigenschaft): boolean =>
    typen(e).some((t) => t === "CELL" || t === "MOBILE" || t === "IPHONE");

/** Bevorzugt markierte Einträge (`PREF`) — das ist die Hauptnummer der Karte. */
function waehle(kandidaten: Eigenschaft[]): string {
    const bevorzugt = kandidaten.find((e) => typen(e).includes("PREF") || "PREF" in e.params);
    return entmaskiere((bevorzugt ?? kandidaten[0])?.wert ?? "").trim();
}

/**
 * Liest ALLE Visitenkarten einer Datei — ein .vcf kann mehrere enthalten
 * (ein exportiertes Adressbuch etwa). Karten ohne jeden brauchbaren Inhalt
 * fallen heraus, damit der Aufrufer nicht auf leeren Masken sitzen bleibt.
 */
export function leseVisitenkarten(text: string): Visitenkarte[] {
    const karten: Visitenkarte[] = [];
    let aktuell: Eigenschaft[] | null = null;

    for (const zeile of entfalte(text)) {
        const e = lesEigenschaft(zeile);
        if (!e) continue;
        if (e.name === "BEGIN" && e.wert.trim().toUpperCase() === "VCARD") {
            aktuell = [];
            continue;
        }
        if (e.name === "END" && e.wert.trim().toUpperCase() === "VCARD") {
            if (aktuell) {
                const karte = baueKarte(aktuell);
                if (istBrauchbar(karte)) karten.push(karte);
            }
            aktuell = null;
            continue;
        }
        aktuell?.push(e);
    }
    return karten;
}

function baueKarte(eigenschaften: Eigenschaft[]): Visitenkarte {
    const alle = (name: string) => eigenschaften.filter((e) => e.name === name);
    const erste = (name: string) => entmaskiere(alle(name)[0]?.wert ?? "").trim();

    // N ist „Nachname;Vorname;Weitere;Präfix;Suffix" und damit die verlässliche
    // Quelle. FN ist nur der Anzeigename und wird erst herangezogen, wenn N
    // fehlt oder leer ist — dann getrennt wie ein Absendername.
    const n = (alle("N")[0]?.wert ?? "").split(";").map((t) => entmaskiere(t).trim());
    let nachname = n[0] ?? "";
    let vorname = n[1] ?? "";
    if (!nachname && !vorname) {
        const fn = erste("FN");
        const teile = fn.split(/\s+/).filter(Boolean);
        nachname = teile.length > 1 ? teile[teile.length - 1] : fn;
        vorname = teile.length > 1 ? teile.slice(0, -1).join(" ") : "";
    }

    const tel = alle("TEL");
    return {
        vorname,
        nachname,
        // ORG ist „Firma;Abteilung" — die Abteilung gehört nicht in den Namen.
        firma: (alle("ORG")[0]?.wert ?? "").split(";").map((t) => entmaskiere(t).trim())[0] ?? "",
        email: waehle(alle("EMAIL")),
        telefon: waehle(tel.filter((e) => !istMobil(e))),
        mobil: waehle(tel.filter(istMobil)),
    };
}

/** Eine Karte ohne Namen UND ohne Kontaktweg ist für die Anlage wertlos. */
function istBrauchbar(k: Visitenkarte): boolean {
    return !!(k.nachname || k.vorname || k.firma || k.email || k.telefon || k.mobil);
}

/**
 * Ist dieser Anhang eine Visitenkarte?
 *
 * Geprüft werden Endung UND Inhaltstyp: Outlook verschickt `text/x-vcard`,
 * Apple `text/vcard`, manche Gateways gar nichts Brauchbares — dann trägt nur
 * noch der Dateiname die Information.
 */
export function istVisitenkarte(anhang: { name?: string | null; contentType?: string | null }): boolean {
    const name = (anhang.name ?? "").trim().toLowerCase();
    const typ = (anhang.contentType ?? "").trim().toLowerCase();
    return name.endsWith(".vcf") || typ === "text/vcard" || typ === "text/x-vcard";
}

/**
 * Macht aus den Rohbytes des Anhangs Text.
 *
 * vCard 3.0/4.0 ist UTF-8, vCard 2.1 aber häufig Windows-1252 — und eine
 * falsch dekodierte Karte bringt „Müller" als „MÃ¼ller" in die Stammdaten.
 * Deshalb erst streng als UTF-8 versuchen; erst wenn das scheitert, gilt die
 * Karte als Alt-Format.
 */
export function dekodiereVcf(bytes: ArrayBuffer): string {
    try {
        return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    } catch {
        return new TextDecoder("windows-1252").decode(bytes);
    }
}
